// ============================================================================
// GST SUBMISSIONS — G-1 slice 3b (ADR-0012 §1, ADR-0025 §3)
// ============================================================================
// Every submission under one engagement: which GSTIN, period, branch and
// stream each file fed, as which revision, and what parsing found. Superseded
// revisions are kept and shown on request — nothing is deleted by a revision.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn } from 'components/ui';
import notificationService from 'services/notificationService';
import { gstSubmissionService } from 'api/services/gstSubmissionService';
import { GST_DOCUMENT_TYPES, type GstDataSubmission, type GstEngagement } from 'api/types/gstEngagement.types';
import GstStagingPreviewModal from './GstStagingPreviewModal';

interface Props {
  isOpen: boolean;
  engagement: GstEngagement | null;
  /** Recording a mapping profile follows the `edit` grant. */
  canEdit?: boolean;
  /** Accepting a client's proposed mapping profile follows the `approve` grant. */
  canApprove?: boolean;
  onClose: () => void;
}

const fyLabel = (y: number) => `FY ${y}-${String((y + 1) % 100).padStart(2, '0')}`;

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const typeLabel = (v: string) => GST_DOCUMENT_TYPES.find((t) => t.value === v)?.label ?? v;

const statusBadge = (s: string) => {
  switch (s) {
    case 'ACCEPTED': return { cls: 'badge badge-active', label: 'Accepted' };
    case 'PARSED': case 'NORMALISED': return { cls: 'badge badge-info', label: 'Parsed' };
    case 'SUPERSEDED': return { cls: 'badge badge-inactive', label: 'Superseded' };
    case 'REJECTED': return { cls: 'badge badge-inactive', label: 'Rejected' };
    default: return { cls: 'badge badge-inactive', label: s };
  }
};

const GstSubmissionsModal: React.FC<Props> = ({ isOpen, engagement, canEdit = false, canApprove = false, onClose }) => {
  const [items, setItems] = useState<GstDataSubmission[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<GstDataSubmission | null>(null);

  const load = useCallback(async () => {
    if (!engagement) return;
    setLoading(true);
    try {
      const res = await gstSubmissionService.getAll(engagement.id, { page, pageSize, includeInactive });
      setItems(res.data);
      setTotal(res.pagination.totalItems);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not load the submissions.');
    } finally {
      setLoading(false);
    }
  }, [engagement, page, pageSize, includeInactive]);

  useEffect(() => { if (isOpen) void load(); }, [isOpen, load]);
  useEffect(() => { if (!isOpen) { setPage(1); setIncludeInactive(false); } }, [isOpen]);

  const getRowActions = useCallback((row: GstDataSubmission): ActionMenuItem[] => [
    { id: 'preview', label: 'Preview staging', icon: <IconSystem.eye size={16} />, onClick: () => setPreview(row) },
  ], []);

  const columns: SyutGridColumn<GstDataSubmission>[] = useMemo(() => [
    { id: 'receivedAt', header: 'Parsed', accessorKey: 'receivedAt', size: 120, enableSorting: true, cell: ({ row }: any) => formatDateTime(row.original.receivedAt) },
    { id: 'gstin', header: 'GSTIN', accessorKey: 'gstin', size: 160, enableSorting: true },
    {
      id: 'period', header: 'Period', accessorKey: 'taxPeriodStart', size: 170, enableSorting: true,
      cell: ({ row }: any) => `${formatDate(row.original.taxPeriodStart)} – ${formatDate(row.original.taxPeriodEnd)}`,
    },
    { id: 'sourceRef', header: 'Branch', accessorKey: 'sourceRef', size: 90, enableSorting: true },
    { id: 'documentType', header: 'Stream', accessorKey: 'documentType', size: 170, enableSorting: true, cell: ({ row }: any) => typeLabel(row.original.documentType) },
    { id: 'submissionNo', header: 'Rev', accessorKey: 'submissionNo', size: 60, enableSorting: true },
    {
      id: 'submissionStatus', header: 'Status', accessorKey: 'submissionStatus', size: 110, enableSorting: true,
      cell: ({ row }: any) => { const b = statusBadge(row.original.submissionStatus); return <span className={b.cls}>{b.label}</span>; },
    },
    {
      id: 'rows', header: 'Data rows', size: 100, enableSorting: false,
      cell: ({ row }: any) => {
        const s = (row.original as GstDataSubmission).parseStats;
        return s ? `${s.data.toLocaleString()} / ${s.rows.toLocaleString()}` : '—';
      },
    },
    { id: 'file', header: 'File', accessorKey: 'sourceDocumentFileName', enableSorting: false },
    { id: 'actions', header: '', size: 56, enableSorting: false, cell: ({ row }: any) => <ActionMenu items={getRowActions(row.original)} /> },
  ], [getRowActions]);

  if (!isOpen || !engagement) return null;

  const title = engagement.clientLegalName || engagement.clientName || 'GST engagement';

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-xl">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Submissions — {title} · {fyLabel(engagement.financialYear)}</h2>
          <button className="modal-close" onClick={onClose} title="Close"><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 12 }}>
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
              {loading ? 'Loading…' : `${total} submission(s). A revision supersedes the previous one for its scope; both are kept.`}
            </div>
            <label style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={includeInactive} onChange={(e) => { setIncludeInactive(e.target.checked); setPage(1); }} />
              Show superseded
            </label>
          </div>

          {!loading && items.length === 0 && (
            <div style={{ border: '1px dashed var(--border-color)', borderRadius: 6, padding: '1rem', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
              No submissions yet. Receive a file under <strong>Source documents</strong>, then create a submission from it.
            </div>
          )}

          {items.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <SyutGrid
                data={items as any}
                columns={columns as any}
                enableSorting
                enablePagination
                manualPagination
                totalCount={total}
                currentPage={page - 1}
                pageSize={pageSize}
                onPageChange={(idx: number) => setPage(idx + 1)}
                onPageSizeChange={(size: number) => { setPageSize(size); setPage(1); }}
                emptyMessage="No submissions."
              />
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>

      <GstStagingPreviewModal
        isOpen={preview !== null}
        engagement={engagement}
        submission={preview}
        canSelectProfile={canEdit}
        canApproveProfile={canApprove}
        onClose={() => setPreview(null)}
      />
    </>
  );
};

export default GstSubmissionsModal;
